/**
 * The call transcript the backend serves (GET /v1/calls/{callId}/transcript), made safe to render.
 *
 * The API answers camelCase inside ApiResponse.data ({ callSummary, transferCallSummary, turns:
 * [{ role, text, ts }], sessions }). Older parts of this frontend assumed flat snake_case bodies,
 * so this reads either — and never trusts a field to be the type it should be.
 *
 * Nothing here logs: a transcript is a customer conversation.
 */

export type Speaker = 'agent' | 'customer' | 'system' | 'tool' | 'unknown';

export interface TranscriptLine {
  speaker: Speaker;
  text: string;
  /** Seconds from the start of the conversation, when the line carries a usable time. */
  offsetSeconds: number | null;
}

export interface CallTranscript {
  callSummary: string | null;
  transferCallSummary: string | null;
  lines: TranscriptLine[];
  truncated: boolean;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

export function speakerOf(role: unknown): Speaker {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : '';
  if (['agent', 'assistant', 'bot', 'ai', 'model'].includes(r)) return 'agent';
  if (['user', 'customer', 'caller', 'human', 'client'].includes(r)) return 'customer';
  if (r === 'system') return 'system';
  if (['tool', 'function', 'function_call', 'tool_call'].includes(r)) return 'tool';
  return 'unknown';
}

/** A raw ts as absolute-or-relative seconds: numbers (s, or epoch s/ms), numeric strings, ISO dates. */
function rawSeconds(ts: unknown): number | null {
  if (typeof ts === 'number' && Number.isFinite(ts)) return ts > 1e12 ? ts / 1000 : ts;
  if (typeof ts === 'string' && ts.trim() !== '') {
    const n = Number(ts);
    if (Number.isFinite(n)) return n > 1e12 ? n / 1000 : n;
    const d = Date.parse(ts);
    if (!Number.isNaN(d)) return d / 1000;
  }
  return null;
}

/**
 * Times as offsets from the conversation's start. Small values (< 1 day) are already offsets;
 * epoch values are made relative to the first timed line.
 */
export function offsets(rawTs: unknown[]): Array<number | null> {
  const secs = rawTs.map(rawSeconds);
  const epochs = secs.filter((s): s is number => s !== null && s >= 86400);
  const base = epochs.length ? Math.min(...epochs) : 0;
  return secs.map((s) => (s === null ? null : s >= 86400 ? Math.max(0, s - base) : Math.max(0, s)));
}

/** The response body (axios res.data) as a CallTranscript. */
export function normalizeTranscriptResponse(body: unknown): CallTranscript {
  const outer: any = body && typeof body === 'object' ? body : {};
  const d: any = outer.data && typeof outer.data === 'object' && !Array.isArray(outer.data) ? outer.data : outer;
  let turns: unknown = d.turns ?? d.transcript ?? d.content;
  if (typeof turns === 'string') {
    try {
      turns = JSON.parse(turns);
    } catch {
      turns = [{ role: null, text: turns }];
    }
  }
  const list: any[] = Array.isArray(turns) ? turns : [];
  const kept = list.filter((t) => t && typeof t === 'object' && (str(t.text) || str(t.content)));
  const offs = offsets(kept.map((t) => t.ts ?? t.timestamp));
  const lines: TranscriptLine[] = kept.map((t, i) => ({
    speaker: speakerOf(t.role ?? t.speaker),
    text: (str(t.text) ?? str(t.content)) as string,
    offsetSeconds: offs[i],
  }));
  const sessions: any[] = Array.isArray(d.sessions) ? d.sessions : [];
  return {
    callSummary: str(d.callSummary) ?? str(d.call_summary),
    transferCallSummary: str(d.transferCallSummary) ?? str(d.transfer_call_summary),
    lines,
    truncated: sessions.some((s) => s && s.transcriptTruncated === true),
  };
}

export function isEmptyTranscript(t: CallTranscript): boolean {
  return t.lines.length === 0 && !t.callSummary && !t.transferCallSummary;
}

/** m:ss (or h:mm:ss). */
export function formatOffset(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

export interface Segment {
  text: string;
  match: boolean;
}

/** Text split around case-insensitive matches of the query (no regex: the query is literal). */
export function highlight(text: string, query: string): Segment[] {
  const q = query.trim();
  if (!q) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  const out: Segment[] = [];
  let at = 0;
  for (let i = lower.indexOf(ql); i !== -1; i = lower.indexOf(ql, i + ql.length)) {
    if (i > at) out.push({ text: text.slice(at, i), match: false });
    out.push({ text: text.slice(i, i + ql.length), match: true });
    at = i + ql.length;
  }
  if (at < text.length) out.push({ text: text.slice(at), match: false });
  return out.length ? out : [{ text, match: false }];
}

export function countMatches(t: CallTranscript, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  let n = 0;
  for (const l of t.lines) {
    const lower = l.text.toLowerCase();
    for (let i = lower.indexOf(q); i !== -1; i = lower.indexOf(q, i + q.length)) n++;
  }
  return n;
}

export const SPEAKER_LABEL: Record<Speaker, string> = {
  agent: 'Agent',
  customer: 'Customer',
  system: 'System',
  tool: 'Action',
  unknown: 'Speaker',
};

/** Plain text for the copy button. */
export function transcriptAsText(t: CallTranscript): string {
  const parts: string[] = [];
  if (t.callSummary) parts.push(`Summary: ${t.callSummary}`);
  if (t.transferCallSummary) parts.push(`Transfer summary: ${t.transferCallSummary}`);
  if (parts.length) parts.push('');
  for (const l of t.lines) {
    const when = l.offsetSeconds === null ? '' : `[${formatOffset(l.offsetSeconds)}] `;
    parts.push(`${when}${SPEAKER_LABEL[l.speaker]}: ${l.text}`);
  }
  return parts.join('\n');
}

/** Seek an <audio> element and play from there; false when there is no element to seek. */
export function seekAudio(el: HTMLAudioElement | null | undefined, seconds: number): boolean {
  if (!el) return false;
  const d = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null;
  el.currentTime = d !== null ? Math.min(Math.max(0, seconds), d) : Math.max(0, seconds);
  const p = el.play?.();
  if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => undefined);
  return true;
}
